// Firebase imports
import { db } from '../firebase/initFirebase'
import {
    doc,
    addDoc,
    DocumentReference,
    setDoc,
    collection,
    updateDoc,
    arrayUnion,
    DocumentData,
} from 'firebase/firestore'
// Services&Helper functions
import imageHelperFunctions from '../utils/imageHelperFunctions'
import memoryFormHelperFunctions from '../utils/memoryFormHelperFunctions'
import * as UpChunk from '@mux/upchunk'
// Types
import { User, ObituaryRefType } from '../types/types'
import {
    MemFormData,
    ObitFormData,
    CondFormData,
    GalleryImage,
} from '../types/formTypes'
import helperFunctions from '../utils/helperFunctions'

const stripSpace = (id: string) => {
    if (id.includes('  ')) {
        id = id.replaceAll('  ', ' ')
        return stripSpace(id)
    } else {
        return id
    }
}

// Function to retrieve which shareCards we want to create
const _getCards = (mem: any) => {
    let cards: string[] = ['memory']
    const { announcements } = mem

    announcements.deceased.text && cards.push('deceased')
    announcements.funeral.text && cards.push('funeral')
    announcements.greeting.text && cards.push('greeting')

    return cards
}

const _checkAndRemoveScraped = async (scrapeName: string) => {
    const deleteRes = await fetch('/api/scraper', {
        method: 'DELETE',
        body: JSON.stringify({
            fullName: scrapeName,
        }),
        headers: {
            'Content-Type': 'application/json',
        },
    })
    if (deleteRes.ok) {
        return true
    }
    return false
}

const firestoreWriteService = () => {
    return {
        createMemory: async (data: MemFormData, user: User) => {
            const _addToUser = async () => {
                const memReference = doc(db, 'memories', id)
                await updateDoc(userRef, {
                    memories: arrayUnion(memReference),
                }).catch((err) =>
                    console.error('Appending memory to user failed: ', err)
                )
            }
            const fullName = stripSpace(data.fullName.trim())

            //Reference to the user creating memory
            const userRef = doc(db, 'users', user.uid)
            // Create unique identifier
            const id = memoryFormHelperFunctions.createID(
                fullName,
                data.birthDate,
                data.deathDate
            )

            let imgURL: string | void = ''
            // Create announcements from data
            const announcements =
                await memoryFormHelperFunctions.createAnnouncements(data, id)
            // Add main image to storage
            const mainImg = data.mainImg
            if (mainImg) {
                let imgPath = await imageHelperFunctions.handleUpload(
                    mainImg.file,
                    'memories/' + id,
                    id
                )
                // Get the url of the image
                imgURL = await imageHelperFunctions.getStorageURL(imgPath)
            } else {
                imgURL = data.fallbackImg
            }

            // Do the same for all other images
            let imgList = []

            for (let i = 0; i < data.gallery?.length; i++) {
                const imgFile = data.gallery[i].file
                const imgPath = await imageHelperFunctions.handleUpload(
                    imgFile,
                    'memories/' + id,
                    id
                )
                const url = await imageHelperFunctions.getStorageURL(imgPath)
                imgList.push(url)
            }

            // Create birth and death date
            const birthDate = memoryFormHelperFunctions.createDate(
                data.birthDate
            )
            const deathDate = memoryFormHelperFunctions.createDate(
                data.deathDate
            )

            // Change PlateObj[] to string (or null if empty)
            const bio = data.biography
            const bioText = bio ? JSON.stringify(bio) : null
            // const familyText = data.family ? JSON.stringify(data.family) : null

            const memData = {
                announcements: announcements,
                biography: bioText,
                birth: birthDate,
                date: new Date(),
                death: deathDate,
                fullName: fullName,
                images: imgList,
                profileImg: imgURL,
                relatives: data.relatives || '',
            }

            // Add memory to firestore
            await setDoc(doc(db, 'memories', id), {
                data: memData,
                birthYear: birthDate.getFullYear().toString(),
                creator: user.uid,
                editors: [user.uid],
                obituaries: [],
                shareCards: {
                    memory: '',
                    deceased: '',
                    funeral: '',
                    greeting: '',
                },
            }).catch((err) => console.error('Creating memory failed: ', err))

            // Add memory to user's memory list
            await Promise.all([_addToUser(), _checkAndRemoveScraped(fullName)])

            const cards = _getCards(memData)

            // @ts-ignore
            memData.birth = helperFunctions.getDate(memData.birth)
            // @ts-ignore
            memData.death = helperFunctions.getDate(memData.death)

            fetch('/api/openGraphImage', {
                method: 'POST',
                body: JSON.stringify({
                    memory: memData,
                    mid: id,
                    cards,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            return id
        },
        followMemory: async (mid: string, uid: string) => {
            const memoryRef = doc(db, 'memories', mid)
            await updateDoc(memoryRef, {
                followers: arrayUnion(uid),
            })
        },
        // Create obituary
        createObituary: async (
            data: ObitFormData,
            mid: string,
            user: User,
            fullName: string,
            publishing: boolean,
            oid?: string
        ) => {
            const memoryRef = doc(db, 'memories', mid)
            // Add obituary to memory
            const _addToMemory = async (
                oRef: DocumentReference<DocumentData>
            ) => {
                await updateDoc(memoryRef, {
                    obituaries: arrayUnion(oRef),
                })
            }

            //Add obituary to user
            const _addToUser = async (
                oRef: DocumentReference<DocumentData>
            ) => {
                await updateDoc(userRef, {
                    obituaries: arrayUnion(oRef),
                })
            }

            //Send email to editors who want to get notification
            const _sendNotifications = async (
                oRef: DocumentReference<DocumentData>,
                obit: Omit<
                    ObituaryRefType,
                    | 'oid'
                    | 'extras'
                    | 'creator'
                    | 'date'
                    | 'mainImage'
                    | 'memory'
                >
            ) => {
                const res = await fetch('/api/email/obituaryNotification', {
                    method: 'POST',
                    body: JSON.stringify({
                        oRef: oRef.id,
                        obituary: obit,
                        mid: mid,
                        sender: user.uid,
                    }),
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                })

                if (res) {
                    console.log('Successfully sent emails')
                } else {
                    console.error('Could not send emails :(')
                }
            }

            //Reference to the user creating obituary
            const userRef = doc(db, 'users', user.uid)

            const publishDate = publishing ? new Date() : null

            const obit = {
                text: data.text,
                date: publishDate,
                creator: userRef,
                fullName: fullName,
                memory: memoryRef,
                visible: publishing,
                videos: data.videos,
            }

            // Add obituary to firestore
            let oReference: DocumentReference<DocumentData>
            if (oid) {
                await setDoc(doc(db, 'obituaries', oid), obit)
                oReference = doc(db, 'obituaries', oid)
            } else {
                oReference = await addDoc(collection(db, 'obituaries'), obit)
            }
            let gallery: Array<string> = []
            let mainImage: string = ''

            // Map through gallery and upload all images of type File: new images.
            gallery = await Promise.all(
                data.gallery.map(async (i) => {
                    if (i.type === 'file') {
                        const imgPath = await imageHelperFunctions.handleUpload(
                            i.file,
                            'obit/' + oReference.id,
                            mid
                        )
                        // Get the url of the image
                        const newImg =
                            (await imageHelperFunctions.getStorageURL(
                                imgPath
                            )) as string
                        if (i.mainImg) mainImage = newImg
                        return newImg
                    } else if (i.type === 'url') {
                        if (i.mainImg) mainImage = i.url
                        return i.url
                    }
                })
            )

            const gall = {
                mainImage: mainImage,
                gallery: gallery,
            }
            await updateDoc(doc(db, 'obituaries', oReference.id), gall)

            // Add obituary reference to user and memory
            await Promise.all([
                publishing && _addToMemory(oReference),
                _addToUser(oReference),
                publishing &&
                    _sendNotifications(oReference, {
                        ...obit,
                        ...gall,
                        text: obit.text as string,
                    }),
            ])

            if (!publishing) {
                return {
                    oid: oReference.id,
                    gallery: gallery.map((i) => {
                        return { type: 'url', url: i, mainImg: i === mainImage }
                    }) as GalleryImage[],
                    title: data.title,
                    videos: data.videos,
                } as ObitFormData
            }

            return oReference.id
        },
        addVideo: async (
            file: File,
            uploadData: { id: string; url: string },
            updateProgress: Function,
            plateCallback: Function,
            formCallback: Function
        ) => {
            let status = ''

            // Start uploading file to MUX
            const upload = UpChunk.createUpload({
                endpoint: uploadData.url,
                file,
            })

            // Event listeners
            upload.on('error', (err) => {
                status = 'error'
                console.log('Error ', err)
            })

            upload.on('progress', (progress) => {
                if (status !== 'success' && status !== 'error') {
                    status = 'progress'
                    if (progress.detail <= 50) {
                        updateProgress(Math.floor(progress.detail * 2))
                    } else {
                        updateProgress((p: number) =>
                            p < progress.detail
                                ? Math.floor(progress.detail)
                                : p
                        )
                    }
                }
            })

            upload.on('success', async () => {
                // Get video displayID and assetID
                const data = await fetch('/api/video', {
                    method: 'POST',
                    body: JSON.stringify({
                        uploadID: uploadData.id,
                    }),
                })
                if (data.ok) {
                    const videoData = await data.json()

                    // TODO: save to firestore
                    await plateCallback(videoData.displayID)
                    formCallback(videoData)
                }
            })
        },
        createCondolence: async (
            data: CondFormData,
            mid: string,
            user: User
        ) => {
            // Create Condolence object
            const condolences = {
                sender: { user: user.uid, name: user.name },
                text: data.text,
                visible: true,
                date: new Date(),
            }
            // Add condolence to firestore
            const condRef = await addDoc(
                collection(db, `memories/${mid}/condolences`),
                {
                    ...condolences,
                }
            ).catch(() => null)

            if (condRef) {
                const res = await fetch('/api/email/condolencesNotification', {
                    method: 'POST',
                    body: JSON.stringify({
                        condolences,
                        mid: mid,
                        sender: user.uid,
                    }),
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                }).catch((err) => console.error(err))
                if (res) {
                    console.log('success!')
                }
            } else {
                console.log('no email')

                return null
            }

            return {
                id: condRef.id,
                ...condolences,
            }
        },
        createRequest: async (user: User, requests: any, mid: string) => {
            const userRef = doc(db, 'users', user.uid)
            const memRef = doc(db, 'memories', mid)
            let updatedReqs = requests
            updatedReqs.sent.push(memRef)
            await updateDoc(userRef, {
                requests: updatedReqs,
            })
        },
    }
}
export default firestoreWriteService()
